// Auth queries + mutations. Backed by TanStack Query so caching is consistent.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';

export type Me = {
  user: { id: string; email: string; name: string; emailVerified: boolean };
  session: { id: string; expiresAt: string };
};

export const meQueryKey = ['me'] as const;

export function useMe() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: () => apiFetch<Me | null>('/me', { throwOn401: false }),
    // Re-poll on window focus is nice for "did my session expire" UX.
    staleTime: 30_000,
  });
}

export function useSignIn() {
  return useMutation({
    mutationFn: async (input: string | { email: string; callbackURL?: string }) => {
      const args = typeof input === 'string' ? { email: input } : input;
      // Default: magic link returns the user to /auth/callback. Callers
      // can pass an explicit callbackURL to encode extra state — e.g.
      // /auth/callback?diagnostic=TOKEN for the cross-domain handoff.
      const callbackURL = args.callbackURL ?? `${window.location.origin}/auth/callback`;
      return await apiFetch<{ status: boolean }>('/auth/sign-in/magic-link', {
        method: 'POST',
        body: { email: args.email, callbackURL },
      });
    },
  });
}

/**
 * Dev-only one-click sign-in. POSTs to /auth/dev-sign-in which (only when
 * api NODE_ENV !== 'production') generates a magic-link token for a fixed
 * dev email, verifies it server-side, and returns the session cookie.
 *
 * The button using this hook is gated by `import.meta.env.DEV` on the
 * client side too — so it disappears entirely from prod bundles.
 */
export function useDevSignIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: true; email: string }>('/auth/dev-sign-in', {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      // Drop cached /me=null so the SPA picks up the new session on next read.
      qc.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

export function useSignOut() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ success: boolean }>('/auth/sign-out', { method: 'POST', body: {} }),
    onSettled: () => {
      // Remove queries rather than invalidating: refetching after sign-out
      // just produces 401s with no consumer. setQueryData(meQueryKey, null)
      // keeps useMe responsive without a network round-trip.
      qc.removeQueries();
      qc.setQueryData(meQueryKey, null);
    },
  });
}

/**
 * Pause ("delete my account"). The api flips user.pausedAt and signs the
 * session out. Subsequent requests get 410 with code: 'account_paused'.
 * Operator hard-deletes manually within 30 days. Phase 9.6.
 */
export function usePauseAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ ok: true }>('/me/pause', { method: 'POST', body: {} }),
    onSettled: () => {
      qc.removeQueries();
      qc.setQueryData(meQueryKey, null);
    },
  });
}
